import { AgentProfileEntity, AgentProfileEntityModel } from "./agent-profile-entity-model";
import { Observable } from "@reactivex/rxjs";
import { LoggingService } from "../loggingSvc";
import { ServiceDeskConfig, caching } from "../../Startup/serviceDeskConfig";
import { RootStoreImplementation } from "../entity/entity-root-store-implementation";
import { ApplicationContextService } from "../context/application-context-service";
import { Prerequisite } from "../loading/prerequisite-loading-service";
import { ConfigWithStartAndEnd } from "../../../ShellCommon/logger/http-interceptor";
import { UserTenantService } from "../user-tenant-service";
import { EntityStore } from "../entity/entity-implementations";
import { GraphApiServiceImplementation } from "../graph-api/graph-api-service";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

export interface AgentProfileContractApp {
  code: string;
  message: string;
}

export interface AgentProfileEntityService {
  /**
   * GetAgentProfile returns agent profile for the logged in user.
   */
  GetAgentProfile(forceUpdate?: boolean): Observable<AgentProfileEntity>;

  /**
   * Get the cached agent profile entity from the last load.
   */
  getAgentProfileSync(): AgentProfileEntity;
}

export class AgentProfileEntityServiceImplementation implements AgentProfileEntityService {
  private agentProfile: AgentProfileEntity = undefined;
  private agentId: string = undefined;
  private serviceFailedToLoad = false;
  private agentProfileCache: any;
  private attachedProfileDeferral: ng.IDeferred<any>;
  private isProfileAttached: boolean;
  private readyDeferral: ng.IDeferred<any>;
  private agentProfileEntityStore: EntityStore<AgentProfileEntity>;

  // @ngInject
  constructor(
    private loggingService: LoggingService,
    private $http: ng.IHttpService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private $q: ng.IQService,
    private applicationContextService: ApplicationContextService,
    private userTenantService: UserTenantService,
    private graphApiService: GraphApiServiceImplementation,
    private sdConfig: ServiceDeskConfig,
    // tslint:disable-next-line:variable-name
    CacheFactory: any,
    private entityRootStore: RootStoreImplementation
  ) {
    this.agentProfileEntityStore = entityRootStore.createAndGetStore(
      "agentProfileEntity",
      loggingService
    ) as EntityStore<AgentProfileEntity>;
    this.agentProfileCache = CacheFactory.get("agentProfileEntity");
    if (!this.agentProfileCache) {
      this.agentProfileCache = CacheFactory("agentProfileEntity", {
        maxAge: caching.timing.agentProfile,
        storageMode: caching.mode
      });
    }
    this.readyDeferral = this.$q.defer();
  }

  /**
   * GetAgentProfile returns agent profile for the logged in user.
   * @param forceUpdate forcibly makes a http call to fetch agent profile data and set it to entity store
   */
  public GetAgentProfile(forceUpdate?: boolean): Observable<AgentProfileEntity> {
    // reject this request if user is either on legacyAuth or not in "AgentProfileEntity" flight
    if (this.userTenantService.isUserOnLegacyAuthTenant()) {
      let errorMessage =
        "GetAgentProfile could not be invoked because either the user is on legacyAuth or is not on 'AgentProfileEntity' flight";
      this.loggingService.logError({ message: errorMessage });
      return Observable.throw(errorMessage);
    } else if (this.serviceFailedToLoad && !forceUpdate) {
      let errorMessage = "The agent profile entity service failed to load.";
      this.loggingService.logError({ message: errorMessage });
      return Observable.throw(errorMessage);
    }

    return this.GetAgentProfileAsObservable(forceUpdate);
  }

  public getAgentProfileSync(): AgentProfileEntity {
    return this.agentProfile;
  }

  private GetAgentProfileAsObservable(forceUpdate?: boolean): Observable<AgentProfileEntity> {
    let reason = forceUpdate ? "reloading value" : "creating value";
    let valueContainer = this.agentProfileEntityStore.getOrAddValueContainer(
      this.agentId,
      undefined,
      reason,
      this.getProfileFromAgentService.bind(this),
      forceUpdate
    );
    return valueContainer.asObservable();
  }

  public getProfileFromAgentService(forceUpdate = false): ng.IPromise<AgentProfileEntity> {
    if (!this.agentId) {
      this.$q.reject("Agent Profile service must be called after from prerequisite load callback has completed");
    }

    let url = `${this.sdConfig.agentServiceUrl}agents/${this.agentId}`;
    if (forceUpdate) {
      this.agentProfileCache.remove(url);
    }

    let config = <ConfigWithStartAndEnd>{
      cache: this.agentProfileCache,
      dependencyName: "AgentService",
      operationName: "AgentProfile.Get",
      dependencyOperationName: "AgentProfile.Get"
    };

    config.qosEventCallback = (result: any) => {
      let data = <AgentProfileContractApp>result.data;

      return {
        isSuccess:
          data && data.message != undefined && data.message.toLowerCase().indexOf("unauthorizedexception") >= 0
            ? true
            : undefined
      };
    };

    return this.graphApiService.getAgentProfile().then(
      (response: AgentProfileEntity) => {
        this.serviceFailedToLoad = false;
        this.agentProfile = response;
        return this.agentProfile;
      },
      (response: ng.IHttpPromiseCallbackArg<{}>) => {
        this.loggingService.logError({
          message: "Could not find agent profile for logged in user",
          response: response,
          aadUser: this.msalAuthenticationService.userInfo
        });
        return this.$q.reject(response.data);
      }
    );
  }

  private mapToAgentProfileEntityModel(response: AgentProfileEntityModel) {
    this.agentProfile = {
      aadUpn: response.AadUpn,
      msaPuid: response.MsaPuid,
      encryptedPuid: response.EncryptedPuid,
      sipAddress: response.SipAddress,
      email: response.Email,
      firstName: response.FirstName,
      lastName: response.LastName,
      siteId: response.SiteId,
      siteName: response.SiteName,
      supplierName: response.SupplierName,
      supportedChannels: response.SupportedChannels,
      extensionProperties: response.ExtensionProperties,
      tier: response.Tier,
      isSfbEnabled: response.IsSfbEnabled,
      supportedLocales: response.SupportedLocales,
      supportAreaPaths: response.SupportAreaPaths,
      restrictedAccessPrograms: response.RestrictedAccessPrograms,
      id: response.id,
      name: response.Name,
      createdDateTime: response.CreatedDateTime,
      lastModifiedDateTime: response.LastModifiedDateTime,
      createdBy: response.CreatedBy,
      lastModifiedBy: response.LastModifiedBy,
      isActive: response.IsActive,
      versionNumber: response.VersionNumber,
      type: response.Type,
      imageUrl: `${this.sdConfig.agentServiceUrl}agents/${this.agentId}/photo`
    };
  }

  public whenReady(): ng.IPromise<any> {
    return this.readyDeferral.promise;
  }

  public prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    let loadcallback = this.loadPrerequisite(prerequisite);
    loadcallback.finally(() => {
      if (this.isProfileAttached) {
        this.readyDeferral.resolve();
      }
    });
    return loadcallback;
  }

  public loadPrerequisite(prerequisite: Prerequisite) {
    if (!prerequisite.userProfile) {
      return this.$q.resolve();
    }
    if (
      !angular.isArray(prerequisite.userProfile) ||
      prerequisite.userProfile.find((bool) => {
        return typeof bool != "boolean";
      })
    ) {
      return this.$q.reject(`malformed prerequisite for agentProfile. ${prerequisite}`);
    }
    if (!prerequisite.userProfile.includes(true)) {
      return this.$q.resolve();
    }
    if (!this.userTenantService.isUserOnLegacyAuthTenant()) {
      if (this.attachedProfileDeferral) {
        return this.attachedProfileDeferral.promise;
      }
      this.attachedProfileDeferral = this.$q.defer();
      this.agentId = this.msalAuthenticationService.userInfo.idToken.preferred_username;

      this.GetAgentProfile().subscribe(
        (agentProfile: AgentProfileEntity) => {
          this.applicationContextService.attachAgentProfileDetails(agentProfile);
          this.isProfileAttached = true;
          this.attachedProfileDeferral.resolve();
        },
        (error) => {
          this.serviceFailedToLoad = true;
          this.loggingService.logError({
            message: `An error occurred while loading the agent-profile for AgentId: ${this.agentId}`,
            error: error
          });
          this.attachedProfileDeferral.resolve(); // TODO: Reject this once everyone's profiles have been correctly configured.
        }
      );
      return this.attachedProfileDeferral.promise;
    }
    return this.$q.resolve();
  }
}
